<template>
	<section
		class="overview-container"
		:class="{ 'overview-container--edit': isEditMode }"
		:dir="isRTL ? 'rtl' : 'ltr'"
	>
		<header v-if="isEditMode" class="overview-toolbar">
			<div class="overview-toolbar__actions">
				<button
					type="button"
					class="overview-toolbar__btn overview-toolbar__btn--primary"
					:disabled="isSaving"
					@click="saveLayout"
				>
					{{ $t('dashboard_save_layout') }}
				</button>
				<button type="button" class="overview-toolbar__btn" :disabled="isSaving" @click="cancelEdit">
					{{ $t('dashboard_cancel_edit') }}
				</button>
				<button
					type="button"
					class="overview-toolbar__btn overview-toolbar__btn--muted"
					:disabled="isSaving"
					@click="resetToDefault"
				>
					{{ $t('dashboard_reset_default') }}
				</button>
			</div>

			<div v-if="showEditHint" class="overview-toolbar__hint" role="status">
				<svg class="overview-toolbar__hint-icon" viewBox="0 0 24 24" aria-hidden="true">
					<path :d="icons.hint" fill="currentColor" />
				</svg>
				<span>{{ $t('dashboard_edit_hint') }}</span>
				<button
					type="button"
					class="overview-toolbar__hint-dismiss"
					:title="$t('dashboard_dismiss_hint')"
					:aria-label="$t('dashboard_dismiss_hint')"
					@click="dismissEditHint"
				>
					<svg viewBox="0 0 24 24" aria-hidden="true">
						<path :d="icons.close" fill="currentColor" />
					</svg>
				</button>
			</div>

			<div class="overview-toolbar__add-panel">
				<span class="overview-toolbar__add-label">{{ $t('dashboard_add_widget') }}</span>
				<div class="overview-toolbar__add-groups">
					<div
						v-for="group in widgetAddPanelGroups"
						:key="group.key"
						class="overview-toolbar__add-group"
					>
						<span class="overview-toolbar__add-group-label">
							<svg class="overview-toolbar__add-group-icon" viewBox="0 0 24 24" aria-hidden="true">
								<path :d="group.icon" fill="currentColor" />
							</svg>
							{{ $t(group.labelKey) }}
						</span>
						<div class="overview-toolbar__add-cards">
							<button
								v-for="{ widgetType, definition } in group.widgets"
								:key="widgetType"
								type="button"
								class="overview-toolbar__add-card"
								:title="$t(definition.descriptionKey)"
								@click="addWidget(widgetType)"
							>
								<span class="overview-toolbar__add-card-icon">
									<svg viewBox="0 0 24 24" aria-hidden="true">
										<path :d="definition.icon" fill="currentColor" />
									</svg>
								</span>
								<span class="overview-toolbar__add-card-text">
									<span class="overview-toolbar__add-card-title">{{ $t(definition.titleKey) }}</span>
									<span class="overview-toolbar__add-card-desc">{{ $t(definition.descriptionKey) }}</span>
								</span>
								<span class="overview-toolbar__add-card-plus" aria-hidden="true">+</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</header>

		<div v-if="!isLayoutReady" class="overview-loading">
			<Loading height="100%" />
		</div>

		<!-- Small screens: single-column stacking, no drag/resize -->
		<div v-else-if="isSmallScreen" class="overview-stacked">
			<div
				v-for="item in stackedLayout"
				:key="item.i"
				class="overview-stacked__item"
				:data-widget-id="item.i"
				:style="{ minHeight: `${item.h * 150}px` }"
			>
				<WidgetHost :item="item" :editMode="false" :widgetData="widgetData" />
			</div>
		</div>

		<!-- vue-grid-layout has no native RTL support: the grid always runs LTR and
			item x positions are mirrored for display in RTL locales (see displayLayout). -->
		<grid-layout
			v-else
			dir="ltr"
			class="overview-grid"
			:layout="displayLayout"
			:col-num="gridColNum"
			:row-height="rowHeight"
			:margin="[10, 10]"
			:is-draggable="isEditMode"
			:is-resizable="isEditMode"
			:vertical-compact="true"
			:use-css-transforms="true"
			@layout-updated="onLayoutUpdated"
		>
			<grid-item
				v-for="item in displayLayout"
				:key="item.i"
				:i="item.i"
				:x="item.x"
				:y="item.y"
				:w="item.w"
				:h="item.h"
				:min-w="getMinW(item)"
				:min-h="getMinH(item)"
				:data-widget-id="item.i"
				drag-allow-from=".widget-host.edit-mode"
				drag-ignore-from=".widget-host__actions, .widget-host__config, .widget-host__config *"
				class="overview-grid-item"
				@moved="onItemMovedOrResized"
				@resized="onItemMovedOrResized"
			>
				<WidgetHost
					:item="item"
					:editMode="isEditMode"
					:widgetData="widgetData"
					:dir="isRTL ? 'rtl' : 'ltr'"
					@remove="removeWidget(item.i)"
					@config-change="updateWidgetConfig(item.i, $event)"
				/>
			</grid-item>
		</grid-layout>
	</section>
</template>

<script>
import VueGridLayout from 'vue-grid-layout'
import WidgetHost from '../widgets/WidgetHost'
import Loading from '../../common/components/Loading'
import { OverviewLineChartsData } from '../services/performanceChartService'
import { getOverviewData, getOverviewSums } from '../services/performanceService'
import { getDeviceCountHistory } from '../../devices/services/deviceService'
import shiftService from '../../control-panel/services/shiftService'
import realTimeService from '../../real-time/services/realTimeService'
import chartService from '../services/performanceChartService'
import util from '../../common/services/utilService'
import { overviewLineChartsColors, trendArrowsBackgroundColors } from '../consts/consts'
import { mdiClose, mdiCursorMove } from '@mdi/js'
import {
	GRID_COL_NUM,
	GRID_ROW_COUNT,
	GRID_ROW_HEIGHT,
	getWidgetAddPanelGroups,
	getWidgetDefinition,
	mirrorLayoutForRtl,
	buildStackedLayout,
	buildWidgetSiteContext,
	filterAvailableLayoutItems,
	reflowLayoutItems
} from '../widgets/widgetRegistry'
import { Toast } from '@/modules/common/Toast'
import { mapGetters } from 'vuex'
import scssVars from '@/styles/vars.scss'
import io from 'socket.io-client/dist/socket.io'

const GridLayout = VueGridLayout.GridLayout
const GridItem = VueGridLayout.GridItem

const datasetMatchingProperties = {
	borderWidth: 1,
	pointHitRadius: 5,
	pointHoverRadius: 5,
	hoverBorderWidth: 3,
	pointRadius: 2
}
const SOCKET_URL = process.env.NODE_ENV === 'production' ? '/' : 'http://localhost:3000'
const SMALL_SCREEN_QUERY = '(max-width: 767px)'

export default {
	data() {
		return {
			chartDataByKey: null,
			realTimeData: null,
			roomsData: null,
			currShift: null,
			socket: null,
			isLoadingRealTime: false,
			isCustomDates: false,
			gridColNum: GRID_COL_NUM,
			rowHeight: GRID_ROW_HEIGHT,
			isSmallScreen: false,
			smallScreenMedia: null,
			trendColors: {
				avg_score: { up: scssVars.trendArrowGreen, down: scssVars.trendArrowRed },
				complaints: { up: scssVars.trendArrowRed, down: scssVars.trendArrowGreen },
				response_time_SLA: { up: scssVars.trendArrowRed, down: scssVars.trendArrowGreen },
				time_saved: { up: scssVars.trendArrowGreen, down: scssVars.trendArrowRed }
			},
			showEditHint: false,
			icons: {
				hint: mdiCursorMove,
				close: mdiClose
			}
		}
	},
	async created() {
		// Load the per-user widget layout in parallel with the dashboard data
		this.$store.dispatch('loadDashboardLayout', { dashboardKey: 'overview' })

		await this.$nextTick();

		if (this.$store.getters.isLoading) {
			return;
		}

		const currentFilter = this.$store.getters.filterSelected;
		if (!currentFilter || !currentFilter.company || !currentFilter.site) {
			return;
		}

		const { company, site, rooms, timeType, time } = currentFilter;

		const latestFilterCheck = this.$store.getters.filterSelected;
		if (latestFilterCheck.company !== company || latestFilterCheck.site !== site) {
			return;
		}

		const isCustomDates =
			timeType === 'custom_time' &&
			time && time.timeFrom && time.timeTo;
		this.isCustomDates = !!isCustomDates;

		const [lastMonthFilter, lastYearFilter] = [util.getTimeByType('last_month'), util.getTimeByType('last_year')];

		const cleanFilter = { company, site, rooms };
		delete cleanFilter.servikName;

		const secondRowTime = isCustomDates ? time : lastYearFilter;

		this.isLoadingRealTime = true;
		const realTimeDataPrm = this.getRealTimeData(site);
		const monthlyDataPrm = getOverviewData({ ...cleanFilter, time: lastMonthFilter, viewTimeBy: 'day' });
		const otherDataPrm = getOverviewData({
			...cleanFilter,
			time: secondRowTime,
			viewTimeBy: 'month'
		});
		const deviceMonthHistoryPrm = getDeviceCountHistory(site, { groupBy: 'month' });
		try {
			const [monthData, otherData, deviceMonthHistory] = await Promise.all([
				monthlyDataPrm,
				otherDataPrm,
				deviceMonthHistoryPrm,
				realTimeDataPrm
			]);

			this.processOverviewData(monthData, otherData, deviceMonthHistory);
			this.updateRoomTableWithRealTime();
		} finally {
			this.isLoadingRealTime = false;
		}
	},
	mounted() {
		this.updateRowHeight();
		window.addEventListener('resize', this.updateRowHeight);
		// vue-material resizes .md-tabs-content (overflow: hidden) from the active
		// tab's height only on DOM mutations — not when vue-grid-layout grows the
		// grid via inline styles (drag/resize below the fold). Sync it ourselves so
		// a grown grid is never clipped and the page can scroll to all widgets.
		if (typeof ResizeObserver !== 'undefined') {
			this.tabsContentResizeObserver = new ResizeObserver(() => this.syncTabsContentHeight());
			this.tabsContentResizeObserver.observe(this.$el);
		}
		this.smallScreenMedia = window.matchMedia(SMALL_SCREEN_QUERY);
		this.isSmallScreen = this.smallScreenMedia.matches;
		if (this.smallScreenMedia.addEventListener) {
			this.smallScreenMedia.addEventListener('change', this.onSmallScreenChange);
		} else {
			this.smallScreenMedia.addListener(this.onSmallScreenChange);
		}
	},
	methods: {
		// ---- layout / edit mode ----
		dismissEditHint() {
			this.showEditHint = false;
		},
		cancelEdit() {
			this.showEditHint = false;
			this.$store.dispatch('cancelDashboardLayoutEdit');
		},
		resetToDefault() {
			this.$store.dispatch('resetDashboardLayoutToDefault');
		},
		async saveLayout() {
			try {
				await this.$store.dispatch('saveDashboardLayout', { dashboardKey: 'overview' });
				this.showEditHint = false;
				new Toast({ message: this.$t('dashboard_layout_saved'), type: 'success', duration: 2500 });
			} catch (error) {
				console.error('[Overview] Failed to save dashboard layout', error);
				new Toast({ message: this.$t('dashboard_layout_save_failed'), type: 'error', duration: 5000 });
			}
		},
		async addWidget(widgetType) {
			const widgetId = await this.$store.dispatch('addDashboardWidget', { widgetType });
			if (widgetId) this.scrollAddedWidgetIntoView(widgetId);
		},
		scrollAddedWidgetIntoView(widgetId) {
			// One tick for DOM update, one frame for vue-grid-layout positioning.
			this.$nextTick(() => {
				requestAnimationFrame(() => {
					const el = this.$el.querySelector(`[data-widget-id="${widgetId}"]`);
					if (!el) return;

					// block: 'nearest' scrolls every scrollable ancestor (the edit-mode
					// container and, if needed, the window) the minimum amount required
					// to reveal the widget, and never scrolls when it is already visible.
					el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
				});
			});
		},
		removeWidget(widgetId) {
			this.$store.commit('removeDashboardWidget', { widgetId });
		},
		updateWidgetConfig(widgetId, config) {
			this.$store.commit('updateDashboardWidgetConfig', { widgetId, config });
		},
		onLayoutUpdated(updatedLayout) {
			// View mode renders a reflowed, display-only copy of the layout — never
			// write those positions back, so the persisted layout stays exactly what
			// the user arranged in edit mode.
			if (!this.isEditMode) return;
			// In RTL the grid renders a mirrored copy; convert back to the canonical
			// LTR coordinates before persisting positions in the store. No reflow here:
			// edit mode must keep the user's explicit sizes and positions.
			const positions = this.isRTL ? mirrorLayoutForRtl(updatedLayout, this.gridColNum) : updatedLayout;
			const positionById = positions.reduce((acc, item) => {
				acc[item.i] = item;
				return acc;
			}, {});
			const layout = this.layout || [];
			let changed = false;
			layout.forEach((item) => {
				const updated = positionById[item.i];
				if (!updated) return;
				if (item.x !== updated.x || item.y !== updated.y || item.w !== updated.w || item.h !== updated.h) {
					item.x = updated.x;
					item.y = updated.y;
					item.w = updated.w;
					item.h = updated.h;
					changed = true;
				}
			});
			if (changed) {
				this.$store.commit('setDashboardLayoutDirty', true);
			}
		},
		onItemMovedOrResized() {
			// In LTR the grid mutates the store layout items in place, so the
			// layout-updated diff never reports a change; mark dirty explicitly.
			if (this.isEditMode) this.$store.commit('setDashboardLayoutDirty', true);
		},
		getMinW(item) {
			const definition = getWidgetDefinition(item.widgetType);
			return definition ? definition.minW : 1;
		},
		getMinH(item) {
			const definition = getWidgetDefinition(item.widgetType);
			return definition ? definition.minH : 1;
		},
		updateRowHeight() {
			// Match the classic layout: 6 rows filling ~75vh (minus the grid margins)
			const available = window.innerHeight * 0.75 - 10 * (GRID_ROW_COUNT + 1);
			this.rowHeight = Math.max(70, Math.round(available / GRID_ROW_COUNT));
		},
		syncTabsContentHeight() {
			// Dashboard renders every tab side by side inside .md-tabs-content; only
			// resize it while the Overview tab is the active one.
			if (this.$route.name !== 'overview') return;
			const tabEl = this.$el.closest('.md-tab');
			const contentEl = this.$el.closest('.md-tabs-content');
			if (!tabEl || !contentEl) return;
			const height = `${tabEl.offsetHeight}px`;
			if (contentEl.style.height !== height) contentEl.style.height = height;
		},
		onSmallScreenChange(event) {
			this.isSmallScreen = event.matches;
		},
		// ---- data fetching / enrichment (unchanged behavior from the fixed Overview) ----
		processOverviewData(monthData, otherData, deviceMonthHistory) {
			if (!monthData || !otherData) return;

			const sortedMonthInstalls = this._sortedDeviceMonthRows(deviceMonthHistory)

			//month charts
			const monthSums = getOverviewSums(monthData)

			const monthOverviewData = new OverviewLineChartsData(JSON.parse(JSON.stringify(monthData)))

			const chartDataByKey = {}
			for (const property in monthOverviewData) {
				const labels = monthOverviewData.labels(property)
				const values =
					property === 'time_saved'
						? monthOverviewData.values(property).map((val) => val && val / 60)
						: monthOverviewData.values(property)
				this.removeLabelsWithoutData(labels, values)
				const { start, end } = chartService.trendStartEnd(values)
				const isTrendUp = start < end
				chartDataByKey[`${property}:lastMonth`] = {
					labels,
					dataset: [
						{
							...datasetMatchingProperties,
							isTrendUp,
							trendArrowBackground: this.trendColors[property][isTrendUp ? 'up' : 'down'],
							sum:
								(property === 'complaints'
									? util.kFormatter(monthSums[property])
									: util.kMinutesToHours(monthSums[property])) +
								(['time_saved', 'response_time_SLA'].includes(property) && monthSums[property] < 999
									? ` ${this.$t('min')}`
									: ''),
							label: this.$t(property),
							data: values,
							pointBackgroundColor: overviewLineChartsColors[property],
							borderColor: overviewLineChartsColors[property],
							pointHoverBackgroundColor: 'white'
						}
					]
				}
			}

			const otherChartData = new OverviewLineChartsData(otherData)
			const otherDataSums = getOverviewSums(otherData)
			for (const property in monthOverviewData) {
				const labels = this.isCustomDates
					? otherChartData.labels(property)
					: otherChartData.labels(property, this.$t('monthMap'))
				const values =
					property === 'time_saved'
						? otherChartData.values(property).map((val) => val && val / 60)
						: otherChartData.values(property)
				let complaintDeviceCounts = null
				if (property === 'complaints') {
					complaintDeviceCounts = (otherData.complaints || []).map((row) =>
						this._cumulativeDevicesAtComplaintMonth(row, sortedMonthInstalls)
					)
					this.removeLabelsWithoutData(labels, values, complaintDeviceCounts)
				} else {
					this.removeLabelsWithoutData(labels, values)
				}
				const { start, end } = chartService.trendStartEnd(values)
				const isTrendUp = start < end
				const chartItem = {
					labels,
					dataset: [
						{
							...datasetMatchingProperties,
							isTrendUp,
							trendArrowBackground: trendArrowsBackgroundColors[property + '_static'],
							trendArrowColor: this.trendColors[property][isTrendUp ? 'up' : 'down'],
							sum:
								(property === 'complaints'
									? util.kFormatter(otherDataSums[property])
									: util.kMinutesToHours(otherDataSums[property])) +
								(['time_saved', 'response_time_SLA'].includes(property) && otherDataSums[property] < 999
									? ` ${this.$t('min')}`
									: ''),
							gradientEnd: overviewLineChartsColors[property + '_static'],
							label: this.$t(property),
							data: values,
							borderColor: 'white',
							pointHoverBorderColor: 'white',
							pointHoverBackgroundColor: overviewLineChartsColors[property + '_static']
						}
					]
				}
				if (complaintDeviceCounts && complaintDeviceCounts.length) {
					chartItem.complaintDeviceCounts = complaintDeviceCounts
				}
				chartDataByKey[`${property}:lastYear`] = chartItem
			}

			this.chartDataByKey = chartDataByKey
		},
		updateRoomTableWithRealTime() {
			const roomsFromAnalytics = this.$store.getters.analyticsData.roomsData;
			this.roomsData = (roomsFromAnalytics || [])
				.map((a) => ({ name: a.displayName, score: a.score, trend: a.trend }))
				.sort((a, b) => a.score - b.score);
			if (this.realTimeData && this.realTimeData.length) {
				this.mergedRoomsDataWithRealTime(this.roomsData, this.realTimeData);
			}
		},
		async getRealTimeData(siteId) {
			if (!siteId) {
				this.realTimeData = [];
				return this.realTimeData;
			}
			const shift = await shiftService.getCurrentShift(siteId);
			if (shift) {
				this.currShift = shift;
				const timeFrom = realTimeService.filterFromByShiftStartHour(shift.start_hour, shift.gmt);
				this.realTimeData = await realTimeService.getRealTimeData(siteId, timeFrom, 'clean', false, false);
			} else {
				this.realTimeData = [];
			}
			return this.realTimeData;
		},
		mergedRoomsDataWithRealTime(roomsData, realTimeData) {
			if (!roomsData || !roomsData.length) return;
			if (!realTimeData || !realTimeData.length) return;
			let copyRoomsData = JSON.parse(JSON.stringify(roomsData))
			const copyRealTimeData = JSON.parse(JSON.stringify(realTimeData))
			const mappedRealTimeData = copyRealTimeData.reduce((acc, room) => {
				if (!acc[room.name]) acc[room.name] = room
				return acc
			}, {})
			copyRoomsData.map((room) => {
				if (mappedRealTimeData[room.name] && mappedRealTimeData[room.name].arrival) {
					room.arrival = mappedRealTimeData[room.name].arrival
				} else room.arrival = { time: '', count: '' }
				room.openTime = mappedRealTimeData[room.name] ? (mappedRealTimeData[room.name].openTime || '') : ''
				room.isOpen = mappedRealTimeData[room.name] ? mappedRealTimeData[room.name].isOpen : false
			})
			copyRoomsData.sort((a, b) => {
				if (a.arrival.time || b.arrival.time) {
					if (b.arrival.time > a.arrival.time) return 1
					if (a.arrival.time > b.arrival.time) return -1
				} else if (a.openTime || b.openTime) {
					if (b.openTime > a.openTime) return 1
					if (a.openTime > b.openTime) return -1
				}
				return 0
			})
			this.roomsData = copyRoomsData
			return copyRoomsData
		},
		async handleIncomingRealTimeData(siteId) {
			if (this.socket) this.socket.disconnect()
			this.socket = io.connect(SOCKET_URL, {
				// withCredentials: true,
				transports: ['websocket'],
				pollingInterval: 10000
			})
			if (this.shift) {
				const timeFrom = realTimeService.filterFromByShiftStartHour(this.shift.start_hour, this.shift.gmt);
				this.socket.emit('site-id', { siteId: siteId, timeFrom: timeFrom.toUTCString() })
				this.socket.on('site-data', ({ cleanRealTimeData }) => {
					const roomsData = this.$store.getters.analyticsData.roomsData
						.map((a) => ({ name: a.displayName, score: a.score, trend: a.trend }))
						.sort((a, b) => a.score - b.score)
					this.mergedRoomsDataWithRealTime(roomsData, cleanRealTimeData)
				})
			}
		},
		_sortedDeviceMonthRows(rows) {
			return [...(rows || [])].sort((a, b) => {
				const ya = Number(a.year)
				const yb = Number(b.year)
				if (ya !== yb) return ya - yb
				return Number(a.time) - Number(b.time)
			})
		},
		_monthlyInstallsThrough(sortedMonthRows, year, month1to12) {
			let sum = 0
			for (const r of sortedMonthRows) {
				const ry = Number(r.year)
				const rm = Number(r.time)
				if (ry < year || (ry === year && rm <= month1to12)) {
					sum += Number(r.count) || 0
				} else {
					break
				}
			}
			return sum
		},
		_cumulativeDevicesAtComplaintMonth(complaintRow, sortedMonthRows) {
			const y = Number(complaintRow.year)
			const m = Number(complaintRow.time)
			if (!y || !m) return null
			return this._monthlyInstallsThrough(sortedMonthRows, y, m)
		},
		removeLabelsWithoutData(labels, values, ...parallelArrays) {
			if (labels.length !== values.length) return
			for (let i = 0; i < labels.length; i++) {
				if (values[i] === false) {
					labels.splice(i, 1)
					values.splice(i, 1)
					parallelArrays.forEach((arr) => {
						if (arr && arr.length > i) arr.splice(i, 1)
					})
					i--
				}
			}
		}
	},
	computed: {
		...mapGetters([
			'analyticsData',
			'overviewData',
			'isLoading',
			'filterSelected',
			'isRTL',
			'dashboardLayout',
			'isDashboardEditMode',
			'isDashboardLayoutLoaded',
			'isSavingDashboardLayout',
			'currentFilteringSite',
			'currentSiteInfo'
		]),
		widgetAddPanelGroups() {
			return getWidgetAddPanelGroups()
		},
		layout() {
			return this.dashboardLayout
		},
		isEditMode() {
			return this.isDashboardEditMode
		},
		isLayoutReady() {
			return this.isDashboardLayoutLoaded && !!this.layout
		},
		isSaving() {
			return this.isSavingDashboardLayout
		},
		widgetSiteContext() {
			return buildWidgetSiteContext({
				currentFilteringSite: this.currentFilteringSite,
				currentSiteInfo: this.currentSiteInfo
			})
		},
		widgetStoreContext() {
			return {
				isLoading: this.isLoading,
				overviewData: this.overviewData,
				analyticsData: this.analyticsData
			}
		},
		visibleLayout() {
			return filterAvailableLayoutItems(
				this.layout || [],
				this.widgetSiteContext,
				this.widgetData,
				this.widgetStoreContext
			)
		},
		displayLayout() {
			// Edit mode: render the stored layout as-is (only site-unavailable widgets
			// are hidden, keeping their slots). No reflow and no data-empty filtering,
			// so the user can freely drag/resize/reorder and nothing snaps back.
			if (this.isEditMode) {
				const editable = filterAvailableLayoutItems(this.layout || [], this.widgetSiteContext);
				return this.isRTL ? mirrorLayoutForRtl(editable, this.gridColNum) : editable;
			}
			// View mode: filter unavailable/empty widgets then reflow each row so the
			// remaining items fill the gaps. This is a display-only copy — the stored
			// layout keeps the hidden widget slots for site switches.
			const layout = reflowLayoutItems(this.visibleLayout, this.gridColNum);
			return this.isRTL ? mirrorLayoutForRtl(layout, this.gridColNum) : layout;
		},
		stackedLayout() {
			return buildStackedLayout(this.visibleLayout)
		},
		dynamicChartDates() {
			if (this.isCustomDates && this.filterSelected && this.filterSelected.time) {
				const { timeFrom, timeTo } = this.filterSelected.time;
				if (timeFrom && timeTo) {
					const fromParts = timeFrom.split('-');
					const toParts = timeTo.split('-');
					if (fromParts.length >= 3 && toParts.length >= 3) {
						return `${fromParts[1]}/${fromParts[0].slice(2, 4)} - ${toParts[1]}/${toParts[0].slice(2, 4)}`;
					}
				}
			}
			return 'last_year';
		},
		/** Shared context passed to every widget (widgets also read the Vuex performance store directly). */
		widgetData() {
			return {
				chartDataByKey: this.chartDataByKey,
				dynamicChartDates: this.dynamicChartDates,
				roomsData: this.roomsData,
				currShift: this.currShift,
				isLoadingRealTime: this.isLoadingRealTime
			}
		}
	},
	watch: {
		isEditMode(value) {
			if (value) this.showEditHint = true;
		}
	},
	destroyed() {
		if (this.socket) this.socket.disconnect()
		window.removeEventListener('resize', this.updateRowHeight)
		if (this.tabsContentResizeObserver) {
			this.tabsContentResizeObserver.disconnect()
			this.tabsContentResizeObserver = null
		}
		if (this.smallScreenMedia) {
			if (this.smallScreenMedia.removeEventListener) {
				this.smallScreenMedia.removeEventListener('change', this.onSmallScreenChange)
			} else {
				this.smallScreenMedia.removeListener(this.onSmallScreenChange)
			}
		}
		// Leaving the page discards unsaved edits, like Cancel
		if (this.isEditMode) {
			this.$store.dispatch('cancelDashboardLayoutEdit')
		}
	},
	components: { WidgetHost, Loading, GridLayout, GridItem }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.overview-container {
	display: flex;
	flex-direction: column;
	min-height: 75vh;

	@include screen-size(xs) {
		min-height: auto;
	}
}

.overview-container--edit {
	// Edit mode scrolls within the dashboard area itself. This keeps the toolbar
	// truly sticky (the surrounding .md-tabs-content has overflow: hidden, so a
	// window-scroll sticky never engages) and keeps a grown grid reachable.
	max-height: 75vh;
	overflow-y: auto;

	.overview-toolbar {
		position: sticky;
		top: 0;
		z-index: 11;
		background-color: #ffffff;
		box-shadow: 0 1px 0 #e9e9e9;
	}
}

.overview-toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 10px;
	padding: 8px 4px;

	@include screen-size(xs) {
		display: none;
	}
}

.overview-toolbar__actions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
}

.overview-toolbar__btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border: 1px solid #d6dae1;
	background: #ffffff;
	color: #4b5563;
	border-radius: 8px;
	padding: 5px 14px;
	font-size: 0.8125rem;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease, color 0.15s ease;

	&:hover:not(:disabled) {
		background: #f3f4f6;
	}

	&--primary {
		background: #389ceb;
		border-color: #389ceb;
		color: #ffffff;

		&:hover:not(:disabled) {
			background: #2d8cd9;
			border-color: #2d8cd9;
		}
	}

	&--muted {
		border-color: transparent;
		background: transparent;
		color: #6b7280;

		&:hover:not(:disabled) {
			background: #f3f4f6;
			color: #4b5563;
		}
	}

	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
}

.overview-toolbar__hint {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex: 1 1 220px;
	min-width: 0;
	padding: 6px 10px;
	border-radius: 8px;
	background: #eff6ff;
	border: 1px solid #bfdbfe;
	color: #1e40af;
	font-size: 0.75rem;
	font-weight: 500;
}

.overview-toolbar__hint-icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	color: #389ceb;
}

.overview-toolbar__hint-dismiss {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: transparent;
	color: #64748b;
	padding: 2px;
	border-radius: 4px;
	cursor: pointer;
	margin-inline-start: auto;
	flex-shrink: 0;

	svg {
		width: 14px;
		height: 14px;
		display: block;
	}

	&:hover {
		background: rgba(0, 0, 0, 0.06);
		color: #334155;
	}
}

.overview-toolbar__add-panel {
	display: flex;
	flex-direction: column;
	gap: 8px;
	flex: 1 1 100%;
	width: 100%;
	padding-top: 4px;
	border-top: 1px solid #eef2f7;
}

.overview-toolbar__add-label {
	font-size: 0.8125rem;
	font-weight: 600;
	color: #374151;
}

.overview-toolbar__add-groups {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
}

.overview-toolbar__add-group {
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
}

.overview-toolbar__add-group-label {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	font-size: 0.7rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #94a3b8;
}

.overview-toolbar__add-group-icon {
	width: 14px;
	height: 14px;
}

.overview-toolbar__add-cards {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.overview-toolbar__add-card {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	border: 1px dashed #cbd5e1;
	background: #f9fafb;
	color: #374151;
	border-radius: 8px;
	padding: 6px 10px;
	font-size: 0.75rem;
	cursor: pointer;
	text-align: start;
	max-width: 220px;
	transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

	&:hover {
		background: #eef2f7;
		border-color: #389ceb;
		box-shadow: 0 2px 8px rgba(56, 156, 235, 0.12);
	}
}

.overview-toolbar__add-card-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 6px;
	background: rgba(56, 156, 235, 0.1);
	color: #389ceb;

	svg {
		width: 16px;
		height: 16px;
		display: block;
	}
}

.overview-toolbar__add-card-text {
	display: flex;
	flex-direction: column;
	gap: 1px;
	min-width: 0;
}

.overview-toolbar__add-card-title {
	font-weight: 600;
	color: #1f2937;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.overview-toolbar__add-card-desc {
	font-size: 0.65rem;
	color: #6b7280;
	line-height: 1.2;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.overview-toolbar__add-card-plus {
	flex-shrink: 0;
	font-size: 1rem;
	font-weight: 600;
	color: #389ceb;
	line-height: 1;
}

.overview-grid {
	// flex-basis auto: the grid's own height (set inline by vue-grid-layout from
	// the number of rows) defines the section height, so a grid that grows past
	// 75vh actually grows the page/scroll area instead of being clipped overflow.
	// flex-grow still stretches it up to the container's 75vh minimum when short.
	flex: 1 0 auto;
}

.overview-grid-item {
	display: flex;
	min-height: 0;

	// Keep the grid interaction affordances above widget content while editing
	&.vue-resizable ::v-deep .vue-resizable-handle {
		z-index: 20;
	}
}

.overview-loading {
	flex: 1;
	min-height: 300px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.overview-stacked {
	display: flex;
	flex-direction: column;
	gap: 10px;

	&__item {
		display: flex;
		flex-direction: column;
		min-height: 0;
	}
}
</style>
